import React, { useEffect, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, Textarea, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { createAttendanceRecord, updateAttendanceRecord } from '../services/api';
import { AttendanceRecord, useAttendanceStoreBase } from '../stores/attendanceStore';
import { useEmployeeOptionsStore, employeeOptions } from '../stores/employeeStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { removeListItem } from '../utils/pendingItem';
import { dateInputValue, timeInputValue, toIsoDateTime, ymd } from '../utils/attendance';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';

interface AttendanceDialogProps {
  record?: AttendanceRecord;
  onClose: () => void;
}

interface FormData {
  employeeId: string;
  date: string;
  checkInTime: string;
  checkOutTime: string;
  overtimeHours: string;
  status: string;
  notes: string;
}

const emptyForm = (): FormData => ({
  employeeId: '', date: ymd(new Date()), checkInTime: '', checkOutTime: '', overtimeHours: '', status: 'PRESENT', notes: '',
});

const STATUSES = ['PRESENT', 'ABSENT', 'LEAVE', 'HOLIDAY'];

const hoursBetween = (checkIn: string, checkOut: string) => {
  if (!checkIn || !checkOut || checkOut <= checkIn) return 0;
  const [h1, m1] = checkIn.split(':').map(Number);
  const [h2, m2] = checkOut.split(':').map(Number);
  return Math.round(((h2 * 60 + m2 - h1 * 60 - m1) / 60) * 10) / 10;
};

/** 新增／編輯出勤：樂觀更新＋立即關閉，工時以後端計算結果為準 */
const AttendanceDialog: React.FC<AttendanceDialogProps> = ({ record, onClose }) => {
  const { t } = useTranslation(['pages', 'translation']);
  const isEditMode = !!record;
  const { employees, fetchAll } = useEmployeeOptionsStore();
  const hoursInput = useNumberInput('decimal');
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>({ defaultValues: emptyForm() });

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useLayoutEffect(() => {
    reset(record ? {
      employeeId: record.employeeId,
      date: dateInputValue(record.date),
      checkInTime: timeInputValue(record.checkInTime),
      checkOutTime: timeInputValue(record.checkOutTime),
      overtimeHours: record.overtimeHours ? String(record.overtimeHours) : '',
      status: record.status || 'PRESENT',
      notes: record.notes || '',
    } : emptyForm());
  }, [record, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, onClose);

  const watchedCheckIn = watch('checkInTime');
  const watchedCheckOut = watch('checkOutTime');
  const workHours = hoursBetween(watchedCheckIn, watchedCheckOut);

  const onSubmit = (data: FormData) => {
    const payload = {
      employeeId: data.employeeId,
      date: `${data.date}T00:00:00.000Z`,
      checkInTime: toIsoDateTime(data.date, data.checkInTime),
      checkOutTime: toIsoDateTime(data.date, data.checkOutTime),
      overtimeHours: data.overtimeHours ? Number(data.overtimeHours) : null,
      status: data.status,
      notes: data.notes.trim(),
    };
    const selected = employees.find(e => e.id === data.employeeId);
    const draft = { ...payload, workHours: workHours || null, employee: selected ? { id: selected.id, name: selected.name, department: selected.department } : record?.employee };
    const store = useAttendanceStoreBase.getState();
    onClose();

    if (record) {
      const original = { ...record };
      store.updateItem(record.id, draft);
      updateAttendanceRecord(record.id, payload)
        .then(response => {
          useAttendanceStoreBase.getState().updateItem(record.id, response.data);
          toast.success(t('pages:attendance.edit.success'));
          notifyDataChanged('attendance');
        })
        .catch(error => {
          useAttendanceStoreBase.getState().updateItem(record.id, original);
          toast.error(apiErrorMessage(error, 'translation:common.messages.updateFailed'));
        });
      return;
    }

    const tempId = `temp-${Date.now()}`;
    store.addItem({ ...draft, id: tempId });
    createAttendanceRecord(payload)
      .then(response => {
        useAttendanceStoreBase.getState().updateItem(tempId, response.data);
        toast.success(t('pages:attendance.add.success'));
        notifyDataChanged('attendance');
      })
      .catch(error => {
        removeListItem(useAttendanceStoreBase, tempId);
        toast.error(apiErrorMessage(error, 'translation:common.messages.createFailed'));
      });
  };

  return (
    <>
      <DialogShell onClose={handleClose} className="max-w-xl">
        <DialogHeader
          title={isEditMode ? t('pages:attendance.edit.title') : t('pages:attendance.add.title')}
          onClose={handleClose}
          closeLabel={t('translation:common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <div className="sm:col-span-2">
              <FormFieldWithError label={t('pages:attendance.form.employee')} required error={errors.employeeId?.message} htmlFor="employeeId">
                <input type="hidden" {...register('employeeId', { required: t('pages:attendance.form.selectEmployee') })} />
                <CustomSelect
                  id="employeeId"
                  value={watch('employeeId')}
                  onChange={value => setValue('employeeId', value, { shouldDirty: true, shouldValidate: true })}
                  variant={errors.employeeId ? 'error' : 'default'}
                  searchable
                  placeholder={t('pages:attendance.form.selectEmployeePlaceholder')}
                  options={employeeOptions(employees, record?.employeeId)}
                />
              </FormFieldWithError>
            </div>
            <FormFieldWithError label={t('pages:attendance.form.date')} required error={errors.date?.message} htmlFor="date">
              <Input id="date" type="date" {...register('date', { required: t('pages:attendance.form.selectDate') })} variant={errors.date ? 'error' : 'default'} />
            </FormFieldWithError>
            <FormFieldWithError label={t('pages:attendance.form.status')} required htmlFor="status">
              <CustomSelect
                id="status"
                value={watch('status')}
                onChange={value => setValue('status', value, { shouldDirty: true })}
                options={STATUSES.map(value => ({ value, label: t(`translation:attendanceStatus.${value}`) }))}
              />
            </FormFieldWithError>
            <FormFieldWithError label={t('pages:attendance.form.checkInTime')} htmlFor="checkInTime">
              <Input id="checkInTime" type="time" {...register('checkInTime')} />
            </FormFieldWithError>
            <FormFieldWithError
              label={t('pages:attendance.form.checkOutTime')}
              error={errors.checkOutTime?.message}
              helpText={workHours > 0 ? t('pages:attendance.form.workHours', { hours: workHours }) : undefined}
              htmlFor="checkOutTime"
            >
              <Input
                id="checkOutTime"
                type="time"
                {...register('checkOutTime', {
                  validate: value => !value || !watchedCheckIn || value > watchedCheckIn || t('pages:attendance.form.checkOutValidation'),
                })}
                variant={errors.checkOutTime ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <FormFieldWithError
              label={t('pages:attendance.form.overtimeHours')}
              error={errors.overtimeHours?.message}
              helpText={t('pages:attendance.form.overtimeHint')}
              htmlFor="overtimeHours"
            >
              <Input
                id="overtimeHours"
                inputMode="decimal"
                className="text-right tabular-nums"
                placeholder="0"
                {...hoursInput.bind(register('overtimeHours', {
                  validate: value => !value || (Number(value) >= 0 && Number(value) <= 24) || t('pages:attendance.form.overtimeInvalid'),
                }))}
                variant={errors.overtimeHours ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <div className="sm:col-span-2">
              <FormFieldWithError label={t('pages:attendance.form.notes')} error={errors.notes?.message} htmlFor="notes">
                <Textarea
                  id="notes"
                  rows={3}
                  {...register('notes', { maxLength: { value: 500, message: t('pages:attendance.form.notesMaxLength') } })}
                  variant={errors.notes ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} className={dialogButtonClass.cancel}>
              {t('translation:common.actions.cancel')}
            </button>
            <button type="submit" className={dialogButtonClass.primary}>
              {isEditMode ? t('translation:common.actions.save') : t('translation:common.actions.create')}
            </button>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('translation:components.modal.unsavedChanges.title')}
        message={t('translation:components.modal.unsavedChanges.message')}
        confirmText={t('translation:components.modal.unsavedChanges.confirmButton')}
        cancelText={t('translation:components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default AttendanceDialog;
